import { useEffect, useMemo, useRef, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import NoDataImage from "assets/images/no-data2.svg";
import { getScribeNoteErrorDisplay } from "features/medical-scribe/components/scribe-note-error.util";
import ScribePromptCollapsible from "features/medical-scribe/components/scribe-prompt-collapsible.component";
import useAudiologyReportSummaryNote from "features/medical-scribe/hooks/use-audiology-report-summary-note.hook";
import { getScribeModalPopoverZIndex } from "features/medical-scribe/medical-scribe-constants";
import MarkdownContainer from "features/patients/sections/patient-ai-summary/components/markdown-container.component";
import useCopyHtmlContentToClipboard from "features/patients/sections/patient-chart-notes/hooks/use-copy-html-content-to-clipboard.hook";
import {
  AMBIENT_SCRIBE_NOTE_PARENT_TYPE,
  AMBIENT_SCRIBE_NOTE_STATUS
} from "services/types/ambient-scribe-note.type";
import { isLikelyHtml } from "utils/markdown.utils";

import AudiologySummaryGenerateSection from "../audiology-summary-generate-section.component";

type AudiologySummaryTabProps = {
  scribeId: number;
  reportId: number;
  /** Union of the local latch and `is_generating_audiology_summary` from the scribe. */
  isGenerating: boolean;
  onGenerateStarted: () => void;
};

/**
 * The Scribe tab when the dialog was opened from an audiology report (SN-19067).
 *
 * Unlike `TextTab`, the content does not live on the scribe at all — an audiology summary is
 * written only to `ai_ambientscribe_notes`, so this reads the notes endpoint and drives its
 * spinner off `is_generating_audiology_summary`, never `is_generating_soap`.
 */
export default function AudiologySummaryTab({
  scribeId,
  reportId,
  isGenerating,
  onGenerateStarted
}: Readonly<AudiologySummaryTabProps>) {
  const theme = useTheme();
  const tooltipPopperProps = useMemo(
    () => ({
      style: { zIndex: getScribeModalPopoverZIndex(theme.zIndex.snackbar) }
    }),
    [theme.zIndex.snackbar]
  );

  const { newest, latestSucceeded, prompt, refetch } =
    useAudiologyReportSummaryNote({ scribeId, reportId, enabled: true });

  // The generation result never touches the scribe, so nothing about the polled scribe payload
  // carries the new note — only the progress flag going false says "a row now exists". Watching
  // that transition is what makes this work with no websocket message at all; the
  // `ambientscribe.note.notification` listener only shortens the wait to the next poll.
  const wasGeneratingRef = useRef(isGenerating);
  useEffect(() => {
    if (wasGeneratingRef.current && !isGenerating) {
      refetch();
    }
    wasGeneratingRef.current = isGenerating;
  }, [isGenerating, refetch]);

  const [showCopied, setShowCopied] = useState(false);
  const copyHtmlContent = useCopyHtmlContentToClipboard();

  // Owned here, not in the generate section: that section is rendered from two different
  // branches below, so it remounts when the first summary arrives and would lose the choice.
  //
  // Three states, not two. `null` already means "the built-in Suno default prompt" — a real,
  // selectable choice — so it cannot also mean "the user hasn't touched this yet". `undefined`
  // carries that, which is what lets the preselect below seed from the note WITHOUT overwriting
  // a user who deliberately picked the built-in prompt on the next 2.5s poll.
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    number | null | undefined
  >(undefined);

  // Preselect whatever produced the summary on screen, so reopening the dialog shows what was
  // actually used rather than resetting to the built-in prompt.
  const effectiveTemplateId =
    selectedTemplateId === undefined
      ? (latestSucceeded?.ambient_scribe_template_id ?? null)
      : selectedTemplateId;

  const summaryHtml = latestSucceeded?.notes ?? "";
  const hasSummary = Boolean(summaryHtml);
  const failure =
    newest?.status === AMBIENT_SCRIBE_NOTE_STATUS.FAILED ? newest : undefined;
  const failureDisplay = failure
    ? getScribeNoteErrorDisplay(
        failure.error,
        AMBIENT_SCRIBE_NOTE_PARENT_TYPE.AUDIOLOGY_REPORT
      )
    : undefined;

  const handleCopy = async () => {
    try {
      await copyHtmlContent(summaryHtml);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy report summary:", err);
    }
  };

  if (isGenerating) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        py={6}
        mt={2}
      >
        <CircularProgress size={32} />
        <Typography variant="body3" color="text.secondary">
          Generating report summary…
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This may take a moment. The summary will appear here automatically.
        </Typography>
      </Stack>
    );
  }

  if (!hasSummary && !failure) {
    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        columnGap={2.5}
        alignItems="center"
        justifyContent="center"
        width="100%"
        minHeight="280px"
        mt={2}
      >
        <Box
          component="img"
          src={NoDataImage}
          alt=""
          sx={{ height: "130px", p: 2 }}
        />
        <Stack rowGap={3}>
          <Typography variant="body3" mb={3}>
            No report summary generated yet. Generate one from this recording.
          </Typography>
          <AudiologySummaryGenerateSection
            scribeId={scribeId}
            reportId={reportId}
            isGenerating={isGenerating}
            hasExistingSummary={false}
            selectedTemplateId={effectiveTemplateId}
            onSelectedTemplateIdChange={setSelectedTemplateId}
            onGenerateStarted={onGenerateStarted}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <Box width="100%" mt={2}>
      {/*
        A failure and a summary can BOTH be present: the newest attempt failed but an earlier one
        succeeded. Hiding the older summary in that case would look like a retry destroyed it.
      */}
      {failure && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="audiology-summary-error"
        >
          {failureDisplay?.message}
          {/* Provider text is a stack-shaped string, so it stays behind a disclosure. */}
          <ScribePromptCollapsible
            prompt={failureDisplay?.detail}
            label="error details"
            testId="scribe-note-error-detail"
            toggleTestId="scribe-note-error-detail-toggle"
          />
        </Alert>
      )}

      {/*
        Omitted rather than rendered empty when there is nothing to copy: with the generate block
        moved to its own row below, an empty row here is 16px of dead space in the
        failure-with-no-summary state.
      */}
      {hasSummary && (
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <Tooltip
            title="Copy text"
            arrow
            disableInteractive
            PopperProps={tooltipPopperProps}
          >
            <IconButton
              onClick={handleCopy}
              color="primary"
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {showCopied && (
            <Typography variant="caption" color="success.main">
              Copied!
            </Typography>
          )}
        </Stack>
      )}

      {/*
        Its own full-width row rather than a third slot in the copy row above. Unlike `TextTab`
        this tab has no right-hand feedback buttons to balance the copy button, so a centered
        middle slot would sit visibly off-centre; a row of its own is centred on the content.
      */}
      <Stack direction="row" justifyContent="center" mb={2}>
        <AudiologySummaryGenerateSection
          scribeId={scribeId}
          reportId={reportId}
          isGenerating={isGenerating}
          hasExistingSummary={hasSummary}
          selectedTemplateId={effectiveTemplateId}
          onSelectedTemplateIdChange={setSelectedTemplateId}
          onGenerateStarted={onGenerateStarted}
        />
      </Stack>

      {hasSummary && (
        <MarkdownContainer html={isLikelyHtml(summaryHtml)}>
          {summaryHtml}
        </MarkdownContainer>
      )}

      <ScribePromptCollapsible prompt={prompt} />
    </Box>
  );
}
